import React from 'react';
import { Users, Wifi, Shield, Clock, CheckCircle } from 'lucide-react';

const Vehicles = () => {
  const fleetStats = [
    { icon: Shield, label: 'Fully Insured', value: '100%' },
    { icon: Clock, label: 'On-Time Performance', value: '99.8%' },
    { icon: Users, label: 'Professional Drivers', value: '15+' },
    { icon: CheckCircle, label: 'Safety Certified', value: 'DOT' }
  ];

  return (
    <section id="vehicles" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-6">Our Modern Fleet</h2>
          <p className="text-xl text-navy-600 max-w-3xl mx-auto leading-relaxed">
            Professional, well-maintained vehicles designed for comfort, safety, and reliability.
            Every vehicle in our fleet is regularly inspected and equipped with modern amenities to ensure a safe and dependable experience for airline crews.
          </p>
        </div>

        {/* Fleet Photos */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <div className="relative rounded-2xl overflow-hidden shadow-xl group">
            <img
              src="/assets/escalade.jpeg"
              alt="Cadillac Escalade ESV - Luxury SUV"
              className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
              <div className="text-white">
                <h3 className="text-2xl font-bold mb-2">Cadillac Escalade ESV</h3>
                <p className="text-sm text-gray-200">Premium luxury SUV with spacious seating for up to 6 passengers</p>
              </div>
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-xl group">
            <img
              src="/assets/Fleet.png"
              alt="Professional Fleet Vehicles"
              className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
              <div className="text-white">
                <h3 className="text-2xl font-bold mb-2">Modern Passenger Vans</h3>
                <p className="text-sm text-gray-200">Comfortable 10-15 passenger vans equipped with modern amenities</p>
              </div>
            </div>
          </div>
        </div>

        {/* Fleet Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {fleetStats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div key={index} className="text-center p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="w-12 h-12 mx-auto bg-primary-100 rounded-lg flex items-center justify-center mb-3">
                  <IconComponent className="w-6 h-6 text-primary-600" />
                </div>
                <div className="text-2xl font-bold text-navy-900 mb-1">{stat.value}</div>
                <div className="text-sm text-navy-600">{stat.label}</div>
              </div>
            );
          })}
        </div>

        {/* Fleet Management Info */}
        <div className="mt-16 bg-white rounded-2xl p-8 md:p-12 shadow-lg">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-bold text-navy-900 mb-6">Safety & Comfort Standards</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Shield className="w-6 h-6 text-primary-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-navy-900">Advanced Safety Monitoring</h4>
                    <p className="text-navy-600 text-sm">Vehicles are equipped with onboard cameras and monitoring systems that track driver performance, including speed and driving behavior, with alerts for any safety concerns.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-primary-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-navy-900">Rigorous Vehicle Inspections</h4>
                    <p className="text-navy-600 text-sm">Each vehicle undergoes safety inspections at the beginning and end of every shift to maintain optimal performance and passenger safety.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Wifi className="w-6 h-6 text-primary-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-navy-900">Complimentary Wi-Fi</h4>
                    <p className="text-navy-600 text-sm">All vehicles provide wireless internet access so passengers can stay connected during transit.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Shield className="w-6 h-6 text-primary-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-navy-900">Passenger Safety Protocols</h4>
                    <p className="text-navy-600 text-sm">Seatbelt compliance is strictly enforced—vehicles remain stationary until all passengers are properly secured.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Shield className="w-6 h-6 text-primary-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-navy-900">Full Insurance Coverage</h4>
                    <p className="text-navy-600 text-sm">All vehicles carry comprehensive commercial insurance and operate in full compliance with DOT safety standards.</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="bg-gradient-to-br from-primary-50 to-navy-50 rounded-xl p-6 mb-6">
                <h4 className="text-lg font-bold text-navy-900 mb-4">Fleet Details</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-navy-600">Vehicle Year:</span>
                    <span className="font-semibold text-navy-900">2022 or Newer</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-navy-600">Total Fleet:</span>
                    <span className="font-semibold text-navy-900">6+ Vehicles</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-navy-600">Passenger Capacity:</span>
                    <span className="font-semibold text-navy-900">10–15 Passengers per Vehicle</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-navy-600">Environment:</span>
                    <span className="font-semibold text-navy-900">Smoke-Free Vehicles</span>
                  </div>
                </div>
              </div>

              <div className="bg-navy-900 rounded-xl p-6 text-white">
                <h4 className="text-lg font-bold mb-3">Professional Drivers</h4>
                <p className="text-gray-300 text-sm">All InTimelog drivers hold valid Texas commercial licenses and undergo continuous training and performance evaluation to maintain the highest standards of safety, professionalism, and service.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Vehicles;